import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { PhoneIcon, PhoneXMarkIcon } from '@heroicons/react/24/outline'
import { RecommendationsContainer } from '../../../interaxDesignSystemComponents'
import { SectionHeader } from './cards'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useWherebyRoom } from '../../../shared/hooks/useWherebyRoom'
import { GroupCallEmptyState } from './whereby/GroupCallEmptyState'
import { PermissionsWarningBanner } from './whereby/PermissionsWarningBanner'
import { useGlobalWhereby } from '../../../shared/hooks/useGlobalWhereby'
import ButtonPrimary from '../../../interaxDesignSystemComponents/ButtonPrimary'
import { INTERAX_COLORS } from '../../../constants/colors'
import { GameDesktopOnlyModal } from './GameDesktopOnlyModal'
import { useMobileGameGuard } from '../hooks/useMobileGameGuard'
import { buildSprintCallPath } from '../../../config/routes/routeConfig'

interface WhereByGroupCallComponentProps {
  className?: string
}

export const WhereByGroupCallComponent: React.FC<WhereByGroupCallComponentProps> = ({ className }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLoading, hasScope, isGameScope, sprintCall } = useWherebyRoom()
  const { isCallActive, startCall, restoreCall } = useGlobalWhereby()
  const { guardGameEntry, isDesktopOnlyModalOpen, closeDesktopOnlyModal } = useMobileGameGuard()

  // A call of the whole sprint is held on its own page; any other call starts here.
  const isEmpty = !isLoading && !hasScope && !sprintCall
  const canStartTeamCall = hasScope && !isCallActive
  const canStartCall = !isLoading && (!!sprintCall || canStartTeamCall)
  const isInCall = !isLoading && hasScope && isCallActive

  // A GAME activity's call is desktop-only: on phones/tablets show the desktop-only notice instead of
  // joining. Team-room and AI-coach calls are NOT game calls and stay allowed on mobile.
  const handleStartCall = () => {
    if (sprintCall) {
      navigate(buildSprintCallPath(sprintCall))
      return
    }

    if (isGameScope) {
      guardGameEntry(startCall)
      return
    }
    startCall()
  }

  // Gate on whether an activity team is resolved (hasScope), exactly like the "Enter team lobby" button.
  // NOT on the team document loading or a pre-created room — that made the lobby show while this stayed
  // empty, and made teammates diverge. Start Call activates the call; the float creates/joins the room.

  return (
    <RecommendationsContainer className={className}>
      <SectionHeader title={t('gamer.groupCall.title')} subtitle={t('gamer.groupCall.subtitle')} />

      {/* Permissions warning — renders only when camera or microphone is blocked */}
      <PermissionsWarningBanner />

      {isLoading && <GroupCallEmptyState variant="loading" />}

      {isEmpty && <GroupCallEmptyState variant="empty" />}

      {canStartCall && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: '200px'
          }}
        >
          <ButtonPrimary
            shape="circle"
            size="large"
            icon={<PhoneIcon className="w-6 h-6" />}
            onClick={handleStartCall}
            ariaLabel={t('gamer.voiceCall.startCall')}
          />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: INTERAX_COLORS.typographyFont
            }}
          >
            {t('gamer.voiceCall.startCall')}
          </Typography>
        </Box>
      )}

      {isInCall && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: '200px'
          }}
        >
          {/* Active call indicator */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: `${INTERAX_COLORS.green}22`,
              border: `1px solid ${INTERAX_COLORS.green}44`
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: INTERAX_COLORS.green,
                flexShrink: 0
              }}
            />
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: INTERAX_COLORS.white }}>
              {t('whereby.global.callActive')}
            </Typography>
          </Box>

          {/* View Call button */}
          <ButtonPrimary
            shape="circle"
            size="large"
            icon={<PhoneXMarkIcon className="w-6 h-6" />}
            onClick={restoreCall}
            ariaLabel={t('gamer.gameCenter.restoreCall')}
          />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: INTERAX_COLORS.typographyFont
            }}
          >
            {t('gamer.gameCenter.restoreCall')}
          </Typography>
        </Box>
      )}

      <GameDesktopOnlyModal isOpen={isDesktopOnlyModalOpen} onClose={closeDesktopOnlyModal} />
    </RecommendationsContainer>
  )
}

export default WhereByGroupCallComponent
